import type { ReactElement } from 'react';

import type {
  BasicContentBlockA,
  BasicContentBlockB,
  BasicContentLayout,
  CategorizationBlock,
  ComparisonSliderBlock,
  CourseBlock,
  ElectronicSignatureBlock,
  LinkBlock,
  MapBlock,
  PolicyAcknowledgementBlock,
  StylizedNumberedListBlock,
  StylizedSidebarCalloutBlock,
  StylizedTextCalloutBlock,
  VideoBlock,
  ClickExpandBlock,
  WorkplaceColorSpectrumBlock,
  LessonCompleteBlock,
  LikertScaleBlock,
  SingleChoiceAnswerBlock,
  FreeformTextBlock,
} from '@course-authoring/shared-types';

import { BasicContentBlockAEditor } from './BasicContentBlockAEditor';
import { BasicContentBlockBEditor } from './BasicContentBlockBEditor';
import { ClickExpandEditor } from './ClickExpandEditor';
import { ComparisonSliderEditor } from './ComparisonSliderEditor';
import { StylizedNumberedListEditor } from './StylizedNumberedListEditor';
import { StylizedSidebarCalloutEditor } from './StylizedSidebarCalloutEditor';
import { StylizedTextCalloutEditor } from './StylizedTextCalloutEditor';
import { VideoBlockEditor } from './VideoBlockEditor';
import { WorkplaceColorSpectrumEditor } from './WorkplaceColorSpectrumEditor';
import { LikertScaleEditor } from './LikertScaleEditor';
import { MapBlockEditor } from './MapBlockEditor';
import { PolicyAcknowledgementEditor } from './PolicyAcknowledgementEditor';
import { LinkEditor } from './LinkEditor';
import { ElectronicSignatureEditor } from './ElectronicSignatureEditor';
import { CategorizationEditor } from './CategorizationEditor';
import { LessonCompleteEditor } from './LessonCompleteEditor';
import { SingleChoiceAnswerEditor } from './SingleChoiceAnswerEditor';
import { FreeformTextEditor } from './FreeformTextEditor';

interface ContentCanvasBlockEditorRenderParams {
  block: CourseBlock;
  autoFocus: boolean;
  onChange: (updates: Partial<CourseBlock>) => void;
  onSetBasicContentLayout: (layout: BasicContentLayout) => void;
  translationMode?: boolean;
}

type ContentCanvasBlockEditorRenderer = (
  params: ContentCanvasBlockEditorRenderParams
) => ReactElement | null;

const contentCanvasBlockEditorRegistry: Partial<
  Record<CourseBlock['type'], ContentCanvasBlockEditorRenderer>
> = {
  video: ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => {
    const videoBlock = block as VideoBlock;

    return (
      <VideoBlockEditor
        block={videoBlock}
        onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
        autoFocus={autoFocus}
        translationMode={translationMode}
      />
    );
  },
  'basic-content-a': ({
    block,
    autoFocus,
    onChange,
    onSetBasicContentLayout,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <BasicContentBlockAEditor
      block={block as BasicContentBlockA}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      onSetLayout={onSetBasicContentLayout}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'basic-content-b': ({
    block,
    autoFocus,
    onChange,
    onSetBasicContentLayout,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <BasicContentBlockBEditor
      block={block as BasicContentBlockB}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      onSetLayout={onSetBasicContentLayout}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'stylized-text-callout': ({
    block,
    autoFocus,
    onChange,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <StylizedTextCalloutEditor
      block={block as StylizedTextCalloutBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'stylized-numbered-list': ({
    block,
    autoFocus,
    onChange,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <StylizedNumberedListEditor
      block={block as StylizedNumberedListBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'stylized-sidebar-callout': ({
    block,
    autoFocus,
    onChange,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <StylizedSidebarCalloutEditor
      block={block as StylizedSidebarCalloutBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'comparison-slider': ({
    block,
    autoFocus,
    onChange,
    translationMode,
  }: ContentCanvasBlockEditorRenderParams) => (
    <ComparisonSliderEditor
      block={block as ComparisonSliderBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'click-expand': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <ClickExpandEditor
      block={block as ClickExpandBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'workplace-color-spectrum': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <WorkplaceColorSpectrumEditor
      block={block as WorkplaceColorSpectrumBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'likert-scale': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <LikertScaleEditor
      block={block as LikertScaleBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  map: ({ block, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <MapBlockEditor
      block={block as MapBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      translationMode={translationMode}
    />
  ),
  'policy-acknowledgement': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <PolicyAcknowledgementEditor
      block={block as PolicyAcknowledgementBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  link: ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <LinkEditor
      block={block as LinkBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'electronic-signature': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <ElectronicSignatureEditor
      block={block as ElectronicSignatureBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  categorization: ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <CategorizationEditor
      block={block as CategorizationBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'single-choice-answer': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <SingleChoiceAnswerEditor
      block={block as SingleChoiceAnswerBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'freeform-text': ({ block, autoFocus, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <FreeformTextEditor
      block={block as FreeformTextBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      autoFocus={autoFocus}
      translationMode={translationMode}
    />
  ),
  'lesson-complete': ({ block, onChange, translationMode }: ContentCanvasBlockEditorRenderParams) => (
    <LessonCompleteEditor
      block={block as LessonCompleteBlock}
      onChange={(updates) => onChange(updates as Partial<CourseBlock>)}
      translationMode={translationMode}
    />
  ),
};

export function renderContentCanvasBlockEditor(
  params: ContentCanvasBlockEditorRenderParams
): ReactElement | null {
  const renderer = contentCanvasBlockEditorRegistry[params.block.type];
  return renderer ? renderer(params) : null;
}

/** Registry-completeness check (TOOL-5 test support). */
export function hasContentCanvasBlockEditor(type: CourseBlock['type']): boolean {
  return contentCanvasBlockEditorRegistry[type] !== undefined;
}
